import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import MessageOutlined from "@mui/icons-material/MessageOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import NightlightIcon from "@mui/icons-material/Nightlight";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Chip, Collapse, IconButton, styled, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { type FC, Fragment, type MouseEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnalyticValuesProvider } from "../../contexts/analytics/AnalyticValuesProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useResolvedActualDay, useWeekPunchSummary, useWeekViewContext } from "../../contexts/WeekView";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { useExpandedGroups } from "../../hooks/useExpandedGroups";
import { toChangeSlot } from "../../hooks/useHandleNurseChange";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRevealWage } from "../../hooks/useRevealWage";
import { useScheduleSaves } from "../../hooks/useScheduleSaves";
import { useShiftWageBreakdown } from "../../hooks/useShiftWageBreakdown";
import type { TranslationKeys } from "../../i18n/types";
import { invalidatePbjPredictionJobCoverage } from "../../pages/PbjModel/hooks/pbjQueryOptions";
import { backendService } from "../../services/backend.service";
import { INK, SHIFT_NIGHT_FALLBACK } from "../../theme/tokens";
import { getActualJobAffordance } from "../../utils/actualJobAffordance";
import { formatLocationLabel } from "../../utils/locationDisplay";
import { type BaseNurseShift, ChangesSource, type Nurse, type NurseInShift } from "../../utils/models";
import { applyShiftJobLocal } from "../../utils/shiftJobUpdate";
import { getShiftIndexByShiftTimes, shiftTypeToIndex } from "../../utils/utilFunctions";
import { canOpenShiftWageBreakdown, isWageBreakdownAvailable } from "../../utils/wageUtils";
import ActualShiftJobHintDialog from "../EditJobPopover/ActualShiftJobHintDialog";
import { EditJobPopover } from "../EditJobPopover/EditJobPopover";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { DeleteRecurringOrSingleShift } from "../NormalizedNurseMenu/DeleteRecurringOrSingleShift/DeleteRecurringOrSingleShift";
import { NurseShiftBadges } from "../NurseShiftBadges/NurseShiftBadges";
import { InvalidActualNursesMenu } from "../nurseContainer/InvalidActualNursesMenu";
import { DirectMessage } from "../StaffCommunication/util/Direct/DirectMessage";
import ColorfulRole from "../UIToolkit/ColorfulRole/ColorfulRole";
import { RawPunchesChip } from "../UIToolkit/Popups/RawPunchesDialog/RawPunchesChip";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { type ReopenCandidate, useReopenWageBreakdownRow } from "../WageBreakdownReturn/useReopenWageBreakdownRow";
import { WageBreakdownSubTable } from "../WageBreakdownSubTable";
import { AddToScheduleDialog } from "./AddToScheduleDialog";
import { formatTotalHours, getShiftTotalHours, getStaffTableColumnWidths, resolveActualRowRemoval } from "./utils";

const StyledNurseTableRow = styled(TableRow)({
	cursor: "pointer",
	height: "36px",
	"& td": {
		fontSize: "0.875rem",
		color: "#4D5579",
		border: "none",
		paddingLeft: "10px",
		paddingRight: "10px",
		backgroundColor: "#FFFFFF",
		borderBottom: "2px solid #E7E9EF",
		textAlign: "center",
	},
	"&:hover td": {
		backgroundColor: "var(--inhouse-primary-alpha-08) !important",
	},
});

type ActualSectionKey =
	| "actual.section.no.punch"
	| "actual.section.not.scheduled"
	| "actual.section.excluded.from.ppd"
	| "actual.section.missing.punch"
	| "actual.section.shift.delta"
	| "actual.section.ot"
	| "actual.section.call.in"
	| "actual.section.agency"
	| "actual.section.staff";

// Compile-time check: ensures all section keys exist in the translation JSON
type ValidatedActualSectionKey = ActualSectionKey & TranslationKeys;

type CallInEntry = { nurseId: number; name: string; reason: string };

type ActualSection =
	| { kind: "shifts"; key: ValidatedActualSectionKey; nurses: BaseNurseShift[] }
	| { kind: "callIn"; key: "actual.section.call.in" & TranslationKeys; entries: CallInEntry[] };

const sectionLength = (s: ActualSection): number => (s.kind === "shifts" ? s.nurses.length : s.entries.length);

/** Stands in for a punch we have not received, so a half punch never reads as a real time or 0 hours. */
const ABSENT_PUNCH_PLACEHOLDER = "—";

/**
 * True only when we genuinely have no time for the absent side: exactly one flag set AND a zero-length
 * row, so both ends sit on the one real punch and printing them just repeats the same time at 0h.
 *
 * The flags alone are NOT enough. The syncher's `_guess_missing_punches` back-fills the absent boundary
 * from the nurse's schedule for ~85% of one-sided punches, and that guessed end is real information —
 * it is what the hours total is computed from — so those rows must keep printing it. A row missing BOTH
 * sides is the never-punched mirror, which carries real scheduled times, and is excluded too.
 */
const hasUnknownPunchSide = (shift: Pick<BaseNurseShift, "missingPunchIn" | "missingPunchOut" | "startTime" | "endTime">): boolean =>
	!!shift.missingPunchIn !== !!shift.missingPunchOut && shift.startTime.toMillis() === shift.endTime.toMillis();

/**
 * Shared look for both interactive states of the Job cell. `inline-block` + the vertical padding
 * keep the hit target usable when the label is short ("--" or a 3-character job).
 */
const interactiveActualJobSx = {
	fontSize: "13px",
	color: INK.SECONDARY,
	cursor: "pointer",
	width: "fit-content",
	display: "inline-block",
	py: 0.25,
	minHeight: "24px",
	"&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "2px", borderRadius: "4px" },
};

type ActualGroupedTableBodyProps = {
	staff: NurseInShift[];
	invalidNurses: BaseNurseShift[];
	date: DateTime;
	shiftDeltaSortOrder: "asc" | "desc";
	onDoubleClickNurse: (nurseId: number) => void;
	/**
	 * The staff member picked in the day's staff search, or null when nobody is picked. Their section
	 * opens itself, and the Call In section — which is not built from `staff` — is narrowed to them.
	 */
	selectedNurseId: number | null;
};

export const ActualGroupedTableBody: FC<ActualGroupedTableBodyProps> = ({
	staff,
	invalidNurses,
	date,
	shiftDeltaSortOrder,
	onDoubleClickNurse,
	selectedNurseId,
}) => {
	const { t } = useTranslation();
	// Names move with the roster (Phase 6).
	const { getNurseNameFromId } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalDemo, isHospitalView } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const { refreshScheduleCache, writeScheduleDay, oldListSlotOf } = useScheduleSaves();
	const { weekViewData, getScheduleNurse, getSchedulePerson, isShiftSalaried } = useWeekViewContext();
	const { track, trackError } = useAnalytics();
	const canViewWage = useRevealWage();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const columnWidths = getStaffTableColumnWidths(isHospitalView || isHospitalDemo);

	const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<HTMLElement>();
	const [actionNurse, setActionNurse] = useState<BaseNurseShift>();
	const [directMessageNurseId, setDirectMessageNurseId] = useState<number>();
	const [addToScheduleShift, setAddToScheduleShift] = useState<NurseInShift>();
	const [expandedRow, setExpandedRow] = useState<string | null>(null);
	// Anchor + shift id for the actual-shift job-edit popover (DAT-502).
	const [editingActualJob, setEditingActualJob] = useState<null | { id: string; element: HTMLElement }>(null);
	// Single-job hint dialog (DAT-615). A modal, so no anchor element — and it renders at the top
	// level rather than inside the cell, since a dialog nested in the click target bubbles its close
	// back through the React tree and re-opens it.
	const [singleJobHint, setSingleJobHint] = useState<null | { nurseId: number; jobName: string }>(null);
	// APP-702: removing a row straight from the Actual view. Same top-level placement (and same reason)
	// as the hint dialog above — nesting it in the kebab's cell bubbles its close back and re-opens it.
	// `isActualRow` picks which table the shared delete dialog writes to (see `resolveActualRowRemoval`).
	const [shiftToRemove, setShiftToRemove] = useState<null | { shift: NurseInShift; isActualRow: boolean }>(null);
	const {
		data: expandedBreakdown,
		isLoading: isBreakdownLoading,
		isError: isBreakdownError,
		refetch: refetchBreakdown,
	} = useShiftWageBreakdown(expandedRow, chosenDepartment.department_id, true, canViewWage);
	const queryClient = useQueryClient();

	const handleActionsMenuOpen = useCallback((event: MouseEvent<HTMLElement>, nurse: BaseNurseShift) => {
		event.stopPropagation();
		setActionsMenuAnchorEl(event.currentTarget);
		setActionNurse(nurse);
	}, []);

	const canSendDirectMessage = useCallback(
		(nurseShift: BaseNurseShift): boolean => {
			const nurse: Nurse | undefined = getScheduleNurse(nurseShift.nurseId);
			return !!nurse?.userId && !!nurse?.phoneNumber;
		},
		[getScheduleNurse],
	);

	const handleRowClick = useCallback((nurse: BaseNurseShift) => {
		setExpandedRow((row) => (row === nurse.id ? null : nurse.id));
	}, []);

	// Reassign an actual shift's job (DAT-502). Optimistically updates hospitalData.nursesActual;
	// the punch summary is derived from that, so the row re-buckets (e.g. into Excluded from PPD)
	// and re-labels without a refetch.
	const handleActualJobChange = useCallback(
		(shiftId: string, nurseId: number, newJobId: string) => {
			setEditingActualJob(null);
			track("shift_job_update_start", { shift_id: shiftId, nurse_id: nurseId, new_job_id: newJobId });
			void backendService
				.updateShiftJob(shiftId, newJobId, true)
				.then((response) => {
					track("shift_job_update_success", { shift_id: shiftId, nurse_id: nurseId });
					invalidatePbjPredictionJobCoverage(queryClient);
					writeScheduleDay(date, (prev) =>
						applyShiftJobLocal(prev, { shiftId, nurseId, newJobId, excludeFromPpd: response.data.excludeFromPpd, isActual: true }),
					);
					void refreshScheduleCache({ dates: [date] });
				})
				.catch((error: unknown) => {
					trackError("shift_job_update_error", {
						shift_id: shiftId,
						nurse_id: nurseId,
						error_message: error instanceof Error ? error.message : String(error),
					});
					showErrorSnackbar(t("failed.to.update.shift.job"), { action: "shift job update failed", source: ChangesSource.DaySchedule }, error);
				});
		},
		[queryClient, writeScheduleDay, refreshScheduleCache, date, showErrorSnackbar, track, trackError, t],
	);

	const scheduledShifts = useMemo(() => {
		const dayItem = weekViewData[date.weekdayISO];
		return dayItem?.shifts ?? [];
	}, [weekViewData, date]);

	const scheduledShiftsByNurseId = useMemo(() => {
		const map = new Map<number, BaseNurseShift[]>();
		for (const shift of scheduledShifts) {
			const list = map.get(shift.nurseId) ?? [];
			list.push(shift);
			map.set(shift.nurseId, list);
		}
		return map;
	}, [scheduledShifts]);

	const punchSummary = useWeekPunchSummary(date);

	// Rows swapped in from the schedule have no punch behind them, so punch-based
	// badges (missing breaks) would be flagging data that was never meant to exist.
	const { substitutedShifts, missingFromScheduled } = useResolvedActualDay(date);
	const substitutedShiftIds = useMemo<Set<string>>(() => new Set(substitutedShifts.map((shift) => shift.id)), [substitutedShifts]);

	// Every row the Actual view renders on the SCHEDULE's behalf — a substitute standing in for a punch
	// we could not use, plus a scheduled shift whose punch never arrived. Their `id` is the scheduled
	// shift's, so a removal has to target the schedule. Built once here rather than per row.
	const scheduledRowIds = useMemo<ReadonlySet<string>>(() => {
		const ids = new Set<string>(substitutedShiftIds);
		for (const shift of missingFromScheduled) ids.add(shift.id);
		return ids;
	}, [substitutedShiftIds, missingFromScheduled]);

	const sections = useMemo<ActualSection[]>(() => {
		// Intersect the provider's punchSummary buckets with the visible `staff`/`invalidNurses`
		// so role/job/time filters from `useShiftFilters` carry through to the day-view sections.
		const staffByNurseId = new Map<number, NurseInShift[]>();
		for (const s of staff) {
			const list = staffByNurseId.get(s.nurseId) ?? [];
			list.push(s);
			staffByNurseId.set(s.nurseId, list);
		}
		const invalidByNurseId = new Map<number, BaseNurseShift[]>();
		for (const i of invalidNurses) {
			const list = invalidByNurseId.get(i.nurseId) ?? [];
			list.push(i);
			invalidByNurseId.set(i.nurseId, list);
		}

		const pickFromInvalid = (nurseId: number): BaseNurseShift[] => invalidByNurseId.get(nurseId) ?? [];
		const pickFromStaff = (nurseId: number): BaseNurseShift[] => staffByNurseId.get(nurseId) ?? [];

		const noPunch = punchSummary.noPunch.flatMap((e) => pickFromInvalid(e.nurseId));
		const notScheduled = punchSummary.notScheduled.employees.flatMap((e) => pickFromStaff(e.nurseId));
		const excludedFromPpd = punchSummary.excludedFromPpd.employees.flatMap((e) => pickFromStaff(e.nurseId));
		const missingPunch = punchSummary.missingPunch.flatMap((e) => pickFromStaff(e.nurseId));
		const shiftDeltaWithDelta = punchSummary.shiftDelta.employees.flatMap((e) => pickFromStaff(e.nurseId).map((shift) => ({ shift, delta: e.diffHours })));
		const initialStaff = punchSummary.staff.flatMap((e) => pickFromStaff(e.nurseId));

		// Default to biggest delta first; flip to ascending only if the user explicitly chose
		// asc on the Shift Delta sort in the picker.
		const sortDir = shiftDeltaSortOrder === "asc" ? 1 : -1;
		const sortedShiftDelta = [...shiftDeltaWithDelta].sort((a, b) => sortDir * (b.delta - a.delta)).map((r) => r.shift);

		// Extract Agency, then OT from the staff catch-all. Agency takes precedence to match the
		// weekly summary (`punchSummary.ts` skips agency shifts in OT calculation).
		const agencyIds = new Set<number>(punchSummary.agency.employees.map((e) => e.nurseId));
		const otIds = new Set<number>(punchSummary.ot.employees.map((e) => e.nurseId));

		// No-punch agency nurses have no actual shift, so they're never in `initialStaff`/`punchSummary.staff` —
		// source them directly from the invalid-nurses lookup, same as the No Punch section above.
		// `punchSummary.agency.employees` is deduped to one entry per nurseId, so take only one
		// representative invalid shift per nurse — otherwise a nurse with several unpunched scheduled
		// shifts the same day would render one Agency row per shift instead of one row per nurse.
		const agencyMissingPunchRows = punchSummary.agency.employees.filter((e) => e.missingPunch).flatMap((e) => pickFromInvalid(e.nurseId).slice(0, 1));
		const agencyRows = [...initialStaff.filter((s) => agencyIds.has(s.nurseId)), ...agencyMissingPunchRows];
		const afterAgency = initialStaff.filter((s) => !agencyIds.has(s.nurseId));
		const otRows = afterAgency.filter((s) => otIds.has(s.nurseId));
		const trueStaff = afterAgency.filter((s) => !otIds.has(s.nurseId));

		// Call-in nurses called off — no actual shift to render. Source directly from punchSummary, so a
		// pick has not narrowed them the way it narrowed `staff`/`invalidNurses`; apply it here.
		const callInEntries: CallInEntry[] =
			selectedNurseId === null ? punchSummary.callIn.employees : punchSummary.callIn.employees.filter((e) => e.nurseId === selectedNurseId);

		const all: ActualSection[] = [];
		if (noPunch.length > 0) all.push({ kind: "shifts", key: "actual.section.no.punch", nurses: noPunch });
		if (notScheduled.length > 0) all.push({ kind: "shifts", key: "actual.section.not.scheduled", nurses: notScheduled });
		if (excludedFromPpd.length > 0) all.push({ kind: "shifts", key: "actual.section.excluded.from.ppd", nurses: excludedFromPpd });
		if (missingPunch.length > 0) all.push({ kind: "shifts", key: "actual.section.missing.punch", nurses: missingPunch });
		if (sortedShiftDelta.length > 0) all.push({ kind: "shifts", key: "actual.section.shift.delta", nurses: sortedShiftDelta });
		if (otRows.length > 0) all.push({ kind: "shifts", key: "actual.section.ot", nurses: otRows });
		if (callInEntries.length > 0) all.push({ kind: "callIn", key: "actual.section.call.in", entries: callInEntries });
		if (agencyRows.length > 0) all.push({ kind: "shifts", key: "actual.section.agency", nurses: agencyRows });
		if (trueStaff.length > 0) all.push({ kind: "shifts", key: "actual.section.staff", nurses: trueStaff });

		return all;
	}, [punchSummary, staff, invalidNurses, shiftDeltaSortOrder, selectedNurseId]);

	// Sections are built from `staff`/`invalidNurses`, which a pick has already narrowed to one person,
	// so a section that still has entries is the section holding them. Empty while nobody is picked.
	const autoExpandedGroups = useMemo<ReadonlySet<string>>(
		() => (selectedNurseId !== null ? new Set(sections.filter((section) => sectionLength(section) > 0).map((section) => section.key)) : new Set()),
		[selectedNurseId, sections],
	);
	const { expandedGroups, setExpandedGroups, toggleGroupExpand } = useExpandedGroups({ autoExpandedGroups });

	// DAT-906 · Returned here by "Back to wage breakdown": open that shift's section and breakdown again.
	const reopenCandidates = useMemo<ReopenCandidate[]>(
		() =>
			sections.flatMap((section) =>
				section.kind === "shifts"
					? section.nurses
							.filter((shift) => canOpenShiftWageBreakdown(shift, getSchedulePerson(shift.nurseId), isShiftSalaried, jobManagementEnabled))
							.map((shift) => ({ shiftId: shift.id, groupKey: section.key }))
					: [],
			),
		[sections, getSchedulePerson, isShiftSalaried, jobManagementEnabled],
	);
	const openSection = useCallback(
		(sectionKey: string) => setExpandedGroups((prev) => (prev.has(sectionKey) ? prev : new Set(prev).add(sectionKey))),
		[setExpandedGroups],
	);
	const reopened = useReopenWageBreakdownRow({
		candidates: reopenCandidates,
		isActual: true,
		openGroup: openSection,
		openRow: setExpandedRow,
		expandedRow,
	});

	const callInReasonByNurseId = useMemo<Map<number, string>>(
		() => new Map((punchSummary?.callIn.employees ?? []).map((e) => [e.nurseId, e.reason])),
		[punchSummary],
	);

	return (
		<>
			{addToScheduleShift && <AddToScheduleDialog nurseShift={addToScheduleShift} date={date} onClose={() => setAddToScheduleShift(undefined)} />}
			{!!directMessageNurseId && (
				<DirectMessage
					open={!!directMessageNurseId}
					onClose={() => setDirectMessageNurseId(undefined)}
					nurseId={directMessageNurseId}
					initialMessage={`Hi, You are missing punches for the ${date.toFormat("MMMM d")} shift at ${chosenDepartment.display_name}. Please reach out to HR or admin team to fix your timesheet`}
				/>
			)}
			{!!shiftToRemove && (
				<DeleteRecurringOrSingleShift
					nurseInShift={shiftToRemove.shift}
					showActual={shiftToRemove.isActualRow}
					// Rows shown here on the schedule's behalf still carry their `recurringShiftId`, so the
					// series radios would render on almost every row of this flow — and picking one calls the
					// recurring endpoint, which deletes the series from this date forward and never passes the
					// actual-removal guard. "Remove from shift" on one day's Actual view must mean that day.
					allowRecurringModes={false}
					source={ChangesSource.DaySchedule}
					onFinished={() => setShiftToRemove(null)}
					onCancel={() => setShiftToRemove(null)}
				/>
			)}
			{!!singleJobHint && (
				<ActualShiftJobHintDialog
					open={true}
					jobName={singleJobHint.jobName}
					onClose={() => setSingleJobHint(null)}
					onGoToProfile={() => {
						track("actual_shift_single_job_hint_go_to_profile", { nurse_id: singleJobHint.nurseId });
						setSingleJobHint(null);
						onDoubleClickNurse(singleJobHint.nurseId);
					}}
				/>
			)}
			{sections.map((section) => {
				const isCollapsed = !expandedGroups.has(section.key);

				return (
					<Fragment key={`actual-section-${section.key}`}>
						<TableRow
							role="button"
							onClick={() => toggleGroupExpand(section.key)}
							sx={{
								cursor: "pointer",
								backgroundColor: "var(--inhouse-primary-dark)",
								"&:hover": { backgroundColor: "var(--inhouse-primary)" },
							}}
						>
							<TableCell colSpan={100} sx={{ px: 1.25, py: 0.5, fontSize: "14px", color: "white", bgcolor: "var(--inhouse-primary-dark)", border: "none" }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<Box sx={{ display: "flex", alignItems: "center" }}>{isCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}</Box>
									<Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Typography component="span" sx={{ fontWeight: 600, width: "11rem" }}>
											{t(section.key)}
										</Typography>
										<Typography component="span" sx={{ fontWeight: 500, minWidth: "5rem", "&::before": { content: "'|'", mx: 0.5 } }}>
											<Box component="span" sx={{ mx: 1, fontWeight: 600 }}>
												{sectionLength(section)}
											</Box>
											staff
										</Typography>
									</Box>
								</Box>
							</TableCell>
						</TableRow>
						<TableRow sx={{ display: isCollapsed ? "none" : undefined }}>
							<TableCell style={{ padding: 0, border: 0 }} colSpan={100}>
								<Collapse in={!isCollapsed} timeout="auto">
									<Table sx={{ tableLayout: "fixed", width: "100%" }}>
										<TableBody>
											{section.kind === "callIn" &&
												section.entries.map((entry) => (
													<StyledNurseTableRow key={`actual-section-callin-${entry.nurseId}`} onDoubleClick={() => onDoubleClickNurse(entry.nurseId)}>
														<TableCell sx={{ width: columnWidths.role.width, minWidth: columnWidths.role.minWidth, textAlign: "left" }}>
															<Typography sx={{ fontSize: "13px", color: "#4D5579" }}>--</Typography>
														</TableCell>
														<TableCell
															sx={{
																width: columnWidths.job?.width,
																minWidth: columnWidths.job?.minWidth,
																"&&": { textAlign: "left", paddingLeft: "16px" },
																verticalAlign: "middle",
															}}
														>
															<Typography sx={{ fontSize: "13px", color: "#4D5579" }}>--</Typography>
														</TableCell>
														<TableCell sx={{ width: columnWidths.name.width, minWidth: columnWidths.name.minWidth, textAlign: "left", pl: 2 }}>
															<InHouseTooltip title={entry.name}>
																<Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
																	<Typography sx={{ fontSize: "14px" }}>{getNurseNameFromId(entry.nurseId)}</Typography>
																</Box>
															</InHouseTooltip>
														</TableCell>
														<TableCell
															sx={{ width: columnWidths.shift.width, minWidth: columnWidths.shift.minWidth, textAlign: "left", verticalAlign: "middle" }}
														>
															<Typography sx={{ fontSize: "14px", color: "#4D5579" }}>--</Typography>
														</TableCell>
														<TableCell
															sx={{ width: columnWidths.location.width, minWidth: columnWidths.location.minWidth, textAlign: "left", verticalAlign: "middle" }}
														>
															<Typography sx={{ fontSize: "14px", color: "#4D5579" }}>--</Typography>
														</TableCell>
														<TableCell
															sx={{ width: columnWidths.notes.width, minWidth: columnWidths.notes.minWidth, textAlign: "left", verticalAlign: "middle" }}
														>
															<Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", flexWrap: "wrap" }}>
																<Chip
																	label={`Call-in: ${entry.reason}`}
																	size="small"
																	data-testid={`actual-badge-callin-${entry.nurseId}`}
																	sx={{ height: 18, fontSize: "11px", bgcolor: "#fb7185", color: "white", "& .MuiChip-label": { px: 0.75 } }}
																/>
															</Box>
														</TableCell>
														<TableCell
															sx={{ width: columnWidths.action.width, minWidth: columnWidths.action.minWidth, textAlign: "left", verticalAlign: "middle" }}
														/>
													</StyledNurseTableRow>
												))}
											{section.kind === "shifts" &&
												section.nurses.map((nurseShift) => {
													const nurseDetails = getScheduleNurse(nurseShift.nurseId);
													// Paid hours, unpaid breaks out: the hours column shows them and the wage breakdown's header repeats them.
													const shiftTotalHours = getShiftTotalHours(
														nurseShift.startTime,
														nurseShift.endTime,
														nurseShift.role,
														nurseShift.type,
														chosenDepartment,
														// Positional gap: the two `undefined`s hold the existing defaults; only the union is new.
														undefined,
														undefined,
														nurseDetails?.union,
													);
													return (
														<AnalyticValuesProvider
															key={`actual-section-nurse-${nurseShift.id}`}
															props={{ nurseId: nurseShift.nurseId, nurseInShiftId: nurseShift.id }}
														>
															<StyledNurseTableRow onDoubleClick={() => onDoubleClickNurse(nurseShift.nurseId)}>
																<TableCell sx={{ width: columnWidths.role.width, minWidth: columnWidths.role.minWidth, textAlign: "left" }}>
																	<ColorfulRole role={nurseShift.role} />
																</TableCell>

																<TableCell
																	sx={{
																		width: columnWidths.job?.width,
																		minWidth: columnWidths.job?.minWidth,
																		"&&": { textAlign: "left", paddingLeft: "16px" },
																		verticalAlign: "middle",
																	}}
																>
																	{(() => {
																		// Multi-job employees reassign the job in place (DAT-502); single-job employees get a
																		// dialog explaining how to add a second job first (DAT-615); no jobs stays inert.
																		const affordance = getActualJobAffordance(nurseDetails?.jobs);
																		if (affordance.kind === "inert") {
																			return <Typography sx={{ fontSize: "13px", color: INK.SECONDARY }}>{nurseShift.jobName || "--"}</Typography>;
																		}
																		const isEditable = affordance.kind === "editable";
																		const tooltip = isEditable ? t("actual.shift.edit.job.tooltip") : t("actual.shift.single.job.tooltip");
																		const openAffordance = (element: HTMLElement) => {
																			if (affordance.kind === "editable") {
																				setEditingActualJob({ id: nurseShift.id, element });
																				return;
																			}
																			track("actual_shift_single_job_hint_opened", {
																				shift_id: nurseShift.id,
																				nurse_id: nurseShift.nurseId,
																				source: "day_view",
																			});
																			setSingleJobHint({ nurseId: nurseShift.nurseId, jobName: affordance.job.displayName });
																		};
																		return (
																			<>
																				<InHouseTooltip title={tooltip}>
																					<Typography
																						onClick={(event) => {
																							event.stopPropagation();
																							openAffordance(event.currentTarget);
																						}}
																						// The row navigates to the profile on double-click, and stopping `click`
																						// does not stop `dblclick` — two fast clicks would otherwise both open
																						// this affordance and navigate away.
																						onDoubleClick={(event) => event.stopPropagation()}
																						role="button"
																						tabIndex={0}
																						aria-label={tooltip}
																						onKeyDown={(event) => {
																							if (event.key !== "Enter" && event.key !== " ") return;
																							event.preventDefault();
																							event.stopPropagation();
																							openAffordance(event.currentTarget);
																						}}
																						data-testid={isEditable ? `actual-shift-job-${nurseShift.id}` : `actual-shift-job-hint-${nurseShift.id}`}
																						sx={
																							isEditable
																								? { ...interactiveActualJobSx, textDecoration: "underline dotted", textUnderlineOffset: "3px" }
																								: interactiveActualJobSx
																						}
																					>
																						{nurseShift.jobName || "--"}
																					</Typography>
																				</InHouseTooltip>
																				{editingActualJob?.id === nurseShift.id && (
																					<EditJobPopover
																						currentJobId={nurseShift.jobId ?? ""}
																						availableJobs={nurseDetails?.jobs ?? []}
																						onJobChange={(newJobId) => handleActualJobChange(nurseShift.id, nurseShift.nurseId, newJobId)}
																						onClose={() => setEditingActualJob(null)}
																						anchorEl={editingActualJob.element}
																					/>
																				)}
																			</>
																		);
																	})()}
																</TableCell>

																<TableCell
																	sx={{
																		width: columnWidths.name.width,
																		minWidth: columnWidths.name.minWidth,
																		textAlign: "left",
																		pl: 2,
																	}}
																>
																	<InHouseTooltip title={nurseShift.name}>
																		<Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
																			<Typography sx={{ fontSize: "14px" }}>{getNurseNameFromId(nurseShift.nurseId)}</Typography>
																		</Box>
																	</InHouseTooltip>
																</TableCell>

																<TableCell
																	sx={{ width: columnWidths.shift.width, minWidth: columnWidths.shift.minWidth, textAlign: "left", verticalAlign: "middle" }}
																>
																	<Box
																		sx={{
																			display: "grid",
																			gridTemplateColumns: "150px 60px 20px",
																			alignItems: "center",
																			columnGap: 1,
																		}}
																	>
																		{(() => {
																			const oneSided = hasUnknownPunchSide(nurseShift);
																			const startText =
																				oneSided && nurseShift.missingPunchIn ? ABSENT_PUNCH_PLACEHOLDER : nurseShift.startTime.toFormat("h:mm a");
																			const endText = oneSided && nurseShift.missingPunchOut ? ABSENT_PUNCH_PLACEHOLDER : nurseShift.endTime.toFormat("h:mm a");
																			const timeText = `${startText} - ${endText}`;
																			const scheduled = scheduledShiftsByNurseId.get(nurseShift.nurseId) ?? [];
																			if (section.key !== "actual.section.shift.delta" || scheduled.length === 0) {
																				return <Typography sx={{ fontSize: "14px" }}>{timeText}</Typography>;
																			}
																			return (
																				<InHouseTooltip
																					title={
																						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
																							<Typography sx={{ fontSize: "12px", fontWeight: 600 }}>Scheduled:</Typography>
																							{scheduled.map((s) => (
																								<Typography key={s.id} sx={{ fontSize: "12px" }}>
																									{s.startTime.toFormat("h:mm a")} - {s.endTime.toFormat("h:mm a")}
																								</Typography>
																							))}
																						</Box>
																					}
																				>
																					<Typography
																						component="span"
																						sx={{ fontSize: "14px", textDecoration: "underline dotted", textUnderlineOffset: "3px", justifySelf: "start" }}
																					>
																						{timeText}
																					</Typography>
																				</InHouseTooltip>
																			);
																		})()}
																		<Typography sx={{ fontSize: "14px", color: "#4D5579" }}>
																			{hasUnknownPunchSide(nurseShift) ? ABSENT_PUNCH_PLACEHOLDER : formatTotalHours(shiftTotalHours)}
																		</Typography>
																		{getShiftIndexByShiftTimes({ startTime: nurseShift.startTime, endTime: nurseShift.endTime }) === 0 ? (
																			<WbSunnyIcon sx={{ color: "#FBBB00", fontSize: 14, opacity: 0.7 }} />
																		) : (
																			<NightlightIcon sx={{ color: SHIFT_NIGHT_FALLBACK, fontSize: 14, opacity: 0.7 }} />
																		)}
																	</Box>
																</TableCell>

																<TableCell
																	sx={{
																		width: columnWidths.location.width,
																		minWidth: columnWidths.location.minWidth,
																		textAlign: "left",
																		verticalAlign: "middle",
																	}}
																>
																	<Box
																		sx={{
																			display: "flex",
																			alignItems: "center",
																			gap: 1,
																			position: "relative",
																			whiteSpace: "nowrap",
																			justifyContent: "flex-start",
																		}}
																	>
																		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{formatLocationLabel(nurseShift.location)}</Box>
																	</Box>
																</TableCell>

																<TableCell
																	sx={{ width: columnWidths.notes.width, minWidth: columnWidths.notes.minWidth, textAlign: "left", verticalAlign: "middle" }}
																>
																	<Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", flexWrap: "wrap" }}>
																		<NurseShiftBadges
																			shift={nurseShift}
																			disableNotOnboardedBadge={true}
																			isActual={true}
																			showMissingBreakPunches={
																				section.key !== "actual.section.no.punch" &&
																				!(section.key === "actual.section.agency" && nurseShift.missingPunchIn && nurseShift.missingPunchOut) &&
																				!substitutedShiftIds.has(nurseShift.id)
																			}
																			canShowLastActualShiftBadge
																		/>
																		{/* DAT-221: support-only raw-punch reveal (renders null for non-support). */}
																		<RawPunchesChip shiftId={nurseShift.id} nurseId={nurseShift.nurseId} />
																		{callInReasonByNurseId.has(nurseShift.nurseId) && (
																			<Chip
																				label={`Call-in: ${callInReasonByNurseId.get(nurseShift.nurseId)}`}
																				size="small"
																				data-testid={`actual-badge-callin-${nurseShift.id}`}
																				sx={{ height: 18, fontSize: "11px", bgcolor: "#fb7185", color: "white", "& .MuiChip-label": { px: 0.75 } }}
																			/>
																		)}
																		<Typography sx={{ fontSize: "14px" }}>{nurseShift.notes || ""}</Typography>
																		{canViewWage && !!nurseShift.bonus && (
																			<Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap", flexShrink: 0 }}>
																				(+${nurseShift.bonus})
																			</Typography>
																		)}
																	</Box>
																</TableCell>

																<TableCell
																	sx={{ width: columnWidths.action.width, minWidth: columnWidths.action.minWidth, textAlign: "left", verticalAlign: "middle" }}
																>
																	<Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
																		{!!nurseShift.startTime &&
																			!!nurseShift.cost &&
																			isWageBreakdownAvailable(getSchedulePerson(nurseShift.nurseId), nurseShift, isShiftSalaried, jobManagementEnabled) && (
																				<IconButton
																					size="small"
																					onClick={(e) => {
																						e.stopPropagation();
																						handleRowClick(nurseShift);
																					}}
																					sx={{ p: "2px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
																				>
																					{expandedRow === nurseShift.id ? (
																						<KeyboardArrowUp sx={{ fontSize: "20px" }} />
																					) : (
																						<KeyboardArrowDown sx={{ fontSize: "20px" }} />
																					)}
																				</IconButton>
																			)}
																		{canSendDirectMessage(nurseShift) && (
																			<SystemButton
																				tooltip="Send Message"
																				sx={{
																					width: "20px",
																					height: "20px",
																					minWidth: "20px",
																					borderRadius: "50%",
																					p: 0,
																					bgcolor: "transparent",
																					"&:hover": {
																						bgcolor: "#D5D2E8",
																					},
																				}}
																				onClick={() => setDirectMessageNurseId(nurseShift.nurseId)}
																				leadingIcon={<MessageOutlined sx={{ fontSize: "18px", color: "#4D5579" }} />}
																			/>
																		)}
																		<IconButton onClick={(event) => handleActionsMenuOpen(event, nurseShift)}>
																			<MoreVert sx={{ fontSize: "20px" }} />
																		</IconButton>
																	</Box>

																	{actionNurse &&
																		actionNurse.id === nurseShift.id &&
																		(() => {
																			// Rows with real punch data behind them simply do not get the item (hide, don't disable).
																			const removal = resolveActualRowRemoval(actionNurse, scheduledRowIds);
																			// The menu's analytics still name the shift's position in the old list.
																			const actionSlot = toChangeSlot(oldListSlotOf(date, actionNurse.shiftType));
																			return (
																				<AnalyticValuesProvider
																					props={{
																						nurseInShiftId: actionNurse.id,
																						nurseId: actionNurse.nurseId,
																						dayNo: actionSlot.dayIndex,
																						shiftNo: shiftTypeToIndex(actionNurse.shiftType),
																						weekIndex: actionSlot.weekIndex,
																					}}
																				>
																					<InvalidActualNursesMenu
																						shiftId={actionNurse.id}
																						nurseId={actionNurse.nurseId}
																						actionsMenuAnchorEl={actionsMenuAnchorEl}
																						onAddToSchedule={
																							section.key === "actual.section.not.scheduled" ? () => setAddToScheduleShift(actionNurse) : undefined
																						}
																						onRemove={
																							removal
																								? () => {
																										track("remove_from_actual_clicked", {
																											nurse_id: actionNurse.nurseId,
																											shift_id: actionNurse.id,
																											is_actual_row: removal.isActualRow,
																										});
																										setShiftToRemove({ shift: actionNurse, isActualRow: removal.isActualRow });
																									}
																								: undefined
																						}
																						onClose={() => {
																							setActionNurse(undefined);
																							setActionsMenuAnchorEl(undefined);
																						}}
																					/>
																				</AnalyticValuesProvider>
																			);
																		})()}
																</TableCell>
															</StyledNurseTableRow>

															{nurseDetails && expandedRow === nurseShift.id && nurseShift.startTime && nurseShift.endTime && (
																<WageBreakdownSubTable
																	columnCount={6}
																	breakdown={expandedBreakdown ?? undefined}
																	isLoading={isBreakdownLoading}
																	isError={isBreakdownError}
																	onRetry={refetchBreakdown}
																	nurse={{ id: nurseDetails.id, firstName: nurseDetails.firstName, name: nurseShift.name, role: nurseShift.role }}
																	paidHours={shiftTotalHours}
																	shiftId={nurseShift.id}
																	autoScrollIntoView={reopened?.shiftId === nurseShift.id}
																	opensWithDetails={reopened?.shiftId === nurseShift.id && reopened.detailsOpen}
																/>
															)}
														</AnalyticValuesProvider>
													);
												})}
										</TableBody>
									</Table>
								</Collapse>
							</TableCell>
						</TableRow>
					</Fragment>
				);
			})}
		</>
	);
};
